import { salesChannelApi } from 'Services/SalesManagement/Features/TicketSale/salesChannelApi'
import {
  ALLOWED_PRODUCT_IDS,
  getProductId,
  PRODUCT_INFO
} from 'Services/SalesManagement/Features/TicketSale/ticketSaleProductInfo'
import type {
  BulkSaleTicketProduct,
  SalesChannelInfo
} from 'Services/SalesManagement/Features/TicketSale/types'
import useTypedSelector, { useAppDispatch } from 'Store'
import { useEffect, useMemo } from 'react'

/**
 * Combines ticket products across all age groups for a given sales channel.
 *
 * For each age group in the provided sales channel info, a product query is initiated
 * in parallel. The results are combined, filtered to only include allowed product IDs,
 * and mapped to the `BulkSaleTicketProduct` format with localised names, descriptions,
 * and info text from `PRODUCT_INFO`.
 *
 * @param salesChannelInfo - The sales channel info containing age groups to fetch products for
 * @param salesChannelId - The external ID of the sales channel to fetch products for.
 *   If undefined, no queries are initiated and the hook returns an empty `allProducts` array with `isLoading` as `false`.
 * @returns Object containing the combined list of products and a loading state flag
 */
function useAllAgeGroupProducts(
  salesChannelInfo: SalesChannelInfo | undefined,
  salesChannelId: string | undefined
) {
  const dispatch = useAppDispatch()

  const ageGroups = useMemo(() => salesChannelInfo?.age_groups ?? [], [salesChannelInfo])

  useEffect(() => {
    if (!salesChannelId) return
    const subscriptions = ageGroups.map(({ id }) =>
      dispatch(
        salesChannelApi.endpoints.getSalesChannelProductsByAgeGroup.initiate({
          salesChannelId,
          ageGroupId: id
        })
      )
    )
    return () => {
      subscriptions.forEach(sub => {
        sub.unsubscribe()
      })
    }
  }, [salesChannelId, ageGroups, dispatch])

  const productQueries = useTypedSelector(state =>
    salesChannelId
      ? ageGroups.map(({ id }) =>
          salesChannelApi.endpoints.getSalesChannelProductsByAgeGroup.select({
            salesChannelId,
            ageGroupId: id
          })(state)
        )
      : []
  )

  const allProducts = useMemo(
    () =>
      ageGroups.flatMap(({ id: age_group_id }, i) =>
        (productQueries[i]?.data ?? [])
          .filter(product => ALLOWED_PRODUCT_IDS.includes(getProductId(product.id)))
          .map<BulkSaleTicketProduct>(product => {
            const shortId = getProductId(product.id)
            const info = PRODUCT_INFO[shortId]
            return {
              id: product.id,
              name: info.name(),
              description: info.description(),
              ageGroupId: age_group_id,
              info: {
                title: info.info.title(),
                body: info.info.body()
              }
            }
          })
      ),
    [ageGroups, productQueries]
  )

  const isLoading = productQueries.some(q => q.isLoading || q.isUninitialized)

  return { allProducts, isLoading }
}

export default useAllAgeGroupProducts
